<script lang="ts">
  import Navbar from "../components/Navbar.svelte";
  import { AppApiClient } from '../api_contracts/app.client';
  import type { NotificationInfo } from '../api_contracts/app';
  import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport';
  import { API_URL } from '../utils/config';

  type NotificationType = 'issue' | 'pr' | 'mention' | 'review' | 'comment' | 'release' | 'fork' | 'star' | 'watch';
  type NotificationStatus = 'unread' | 'read';

  const transport = new GrpcWebFetchTransport({
    baseUrl: API_URL
  });
  const client = new AppApiClient(transport);

  let activeTab = $state<'inbox' | 'new'>('new');
  let selectedFilter = $state<'all' | 'issue' | 'pr' | 'mention' | 'review'>('all');
  let selectedNotifications = $state<Set<string>>(new Set());
  let notifications = $state<NotificationInfo[]>([]);
  let unreadCount = $state(0);
  let totalCount = $state(0);
  let loading = $state(true);
  let error = $state<string | null>(null);

  const token = localStorage.getItem('authToken') || '';

  // Fetch notifications on mount
  $effect(() => {
    fetchNotifications();
  });

  async function fetchNotifications() {
    try {
      loading = true;
      error = null;

      const statusFilter = activeTab === 'new' ? 'unread' : 'all';
      const typeFilter = selectedFilter === 'all' ? '' : selectedFilter;

      const result = await client.getUserNotifications({
        token,
        status: statusFilter,
        type: typeFilter,
        limit: 50,
        offset: 0
      });

      if (result.response.successFull) {
        notifications = result.response.notifications || [];
        unreadCount = result.response.unreadCount || 0;
        totalCount = result.response.totalCount || 0;
      } else {
        error = result.response.reason || 'Failed to fetch notifications';
      }
    } catch (err) {
      error = 'Failed to connect to server';
      console.error('Error fetching notifications:', err);
    } finally {
      loading = false;
    }
  }

  async function markAsRead(id: string) {
    try {
      const result = await client.markNotificationAsRead({
        token,
        notificationId: id
      });

      if (result.response.successFull) {
        // Update local state
        notifications = notifications.map(n =>
          n.id === id ? { ...n, status: 'read' } : n
        );
        unreadCount = Math.max(0, unreadCount - 1);
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }

  async function markAllAsRead() {
    try {
      const result = await client.markAllNotificationsAsRead({ token });

      if (result.response.successFull) {
        // Refresh notifications
        await fetchNotifications();
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }

  async function deleteNotification(id: string) {
    try {
      const result = await client.deleteNotification({
        token,
        notificationId: id
      });

      if (result.response.successFull) {
        // Remove from local state
        notifications = notifications.filter(n => n.id !== id);
        totalCount = Math.max(0, totalCount - 1);
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }

  function toggleSelection(id: string) {
    const newSet = new Set(selectedNotifications);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    selectedNotifications = newSet;
  }

  function getTimeAgo(timestamp: Date | undefined): string {
    if (!timestamp) return 'unknown';

    const now = new Date();
    const notifDate = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - notifDate.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
    return `${Math.floor(seconds / 604800)}w`;
  }

  function getAvatarUrl(username: string): string {
    const colors = ['4f46e5', '059669', 'dc2626', 'ea580c', '7c3aed', '6b7280'];
    const colorIndex = username.length % colors.length;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=${colors[colorIndex]}`;
  }

  // Trigger refetch when tab or filter changes
  $effect(() => {
    // Watch activeTab and selectedFilter
    activeTab;
    selectedFilter;
    fetchNotifications();
  });

  function getTypeIcon(type: NotificationType) {
    const icons = {
      issue: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><path d="M8 9.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/><path fill-rule="evenodd" d="M8 0a8 8 0 100 16A8 8 0 008 0zM1.5 8a6.5 6.5 0 1113 0 6.5 6.5 0 01-13 0z"/></svg>`,
      pr: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M7.177 3.073L9.573.677A.25.25 0 0110 .854v4.792a.25.25 0 01-.427.177L7.177 3.427a.25.25 0 010-.354zM3.75 2.5a.75.75 0 100 1.5.75.75 0 000-1.5zm-2.25.75a2.25 2.25 0 113 2.122v5.256a2.251 2.251 0 11-1.5 0V5.372A2.25 2.25 0 011.5 3.25zM11 2.5h-1V4h1a1 1 0 011 1v5.628a2.251 2.251 0 101.5 0V5A2.5 2.5 0 0011 2.5zm1 10.25a.75.75 0 111.5 0 .75.75 0 01-1.5 0zM3.75 12a.75.75 0 100 1.5.75.75 0 000-1.5z"/></svg>`,
      mention: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>`,
      review: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1.679 7.932c.412-.621 1.242-1.75 2.366-2.717C5.175 4.242 6.527 3.5 8 3.5c1.473 0 2.824.742 3.955 1.715 1.124.967 1.954 2.096 2.366 2.717a.119.119 0 010 .136c-.412.621-1.242 1.75-2.366 2.717C10.825 11.758 9.473 12.5 8 12.5c-1.473 0-2.824-.742-3.955-1.715C2.92 9.818 2.09 8.69 1.679 8.068a.119.119 0 010-.136zM8 2c-1.981 0-3.67.992-4.933 2.078C1.797 5.169.88 6.423.43 7.1a1.619 1.619 0 000 1.798c.45.678 1.367 1.932 2.637 3.024C4.329 13.008 6.019 14 8 14c1.981 0 3.67-.992 4.933-2.078 1.27-1.091 2.187-2.345 2.637-3.023a1.619 1.619 0 000-1.798c-.45-.678-1.367-1.932-2.637-3.023C11.671 2.992 9.981 2 8 2zm0 8a2 2 0 100-4 2 2 0 000 4z"/></svg>`,
      comment: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><path d="M2.678 11.894a1 1 0 01.287.801 10.97 10.97 0 01-.398 2c1.395-.323 2.247-.697 2.634-.893a1 1 0 01.71-.074A8.06 8.06 0 008 14c3.996 0 7-2.807 7-6 0-3.192-3.004-6-7-6S1 4.808 1 8c0 1.468.617 2.83 1.678 3.894zm-.493 3.905a21.682 21.682 0 01-.713.129c-.2.032-.352-.176-.273-.362a9.68 9.68 0 00.244-.637l.003-.01c.248-.72.45-1.548.524-2.319C.743 11.37 0 9.76 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7-3.582 7-8 7a9.06 9.06 0 01-2.347-.306c-.52.263-1.639.742-3.468 1.105z"/></svg>`,
      release: `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M8.5.75a.75.75 0 00-1.5 0V2h-.984c-.305 0-.604.08-.869.23l-1.288.737A.25.25 0 013.984 3H1.75a.75.75 0 000 1.5h.428L.066 9.192a.75.75 0 00.154.838l.53-.53-.53.53v.001l.002.002.002.002.006.006.016.015.045.04a3.514 3.514 0 00.686.45A4.492 4.492 0 003 11c.88 0 1.556-.22 2.023-.454a3.515 3.515 0 00.686-.45l.045-.04.016-.015.006-.006.002-.002.001-.002L5.25 9.5l.53.53a.75.75 0 00.154-.838L3.822 4.5h.162c.305 0 .604-.08.869-.23l1.289-.737a.25.25 0 01.124-.033h.984V13h-2.5a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-2.5V3.5h.984a.25.25 0 01.124.033l1.29.736c.264.152.563.231.868.231h.162l-2.112 4.692a.75.75 0 00.154.838l.53-.53-.53.53v.001l.002.002.002.002.006.006.016.015.045.04a3.517 3.517 0 00.686.45A4.492 4.492 0 0013 11c.88 0 1.556-.22 2.023-.454a3.512 3.512 0 00.686-.45l.045-.04.01-.01.006-.005.006-.006.002-.002.001-.002-.529-.531.53.53a.75.75 0 00.154-.838L13.823 4.5h.427a.75.75 0 000-1.5h-2.234a.25.25 0 01-.124-.033l-1.29-.736A1.75 1.75 0 009.735 2H8.5V.75z"/></svg>`
    };
    return icons[type] || icons.issue;
  }

  function getTypeColor(type: NotificationType) {
    const colors = {
      issue: 'text-green-600',
      pr: 'text-purple-600',
      mention: 'text-blue-600',
      review: 'text-orange-600',
      comment: 'text-gray-600',
      release: 'text-indigo-600'
    };
    return colors[type] || colors.issue;
  }

  const filteredNotifications = $derived(() => {
    return notifications;
  });
</script>

<Navbar/>

<div class="min-h-screen bg-gray-50 dark:bg-gray-950">
  <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Notifications</h1>
      <p class="text-gray-600 dark:text-gray-400">Manage updates from repositories you're watching</p>
    </div>

    <!-- Main Container -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <!-- Navigation Tabs -->
      <div class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div class="flex items-center justify-between px-6 py-3">
          <nav class="flex space-x-1">
            <button
              onclick={() => activeTab = 'inbox'}
              class="px-4 py-2 text-sm font-medium rounded-md transition-all {activeTab === 'inbox' ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'}"
            >
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
                </svg>
                Inbox
              </div>
            </button>
            <button
              onclick={() => activeTab = 'new'}
              class="px-4 py-2 text-sm font-medium rounded-md transition-all {activeTab === 'new' ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'}"
            >
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
                </svg>
                New
                {#if unreadCount > 0}
                  <span class="px-2 py-0.5 text-xs font-bold bg-blue-600 text-white rounded-full">
                    {unreadCount}
                  </span>
                {/if}
              </div>
            </button>
            <!-- <button
              onclick={() => activeTab = 'done'}
              class="px-4 py-2 text-sm font-medium rounded-md transition-all {activeTab === 'done' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}"
            >
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                Done
                {#if doneCount > 0}
                  <span class="text-xs text-gray-500">
                    {doneCount}
                  </span>
                {/if}
              </div>
            </button> -->
          </nav>
        </div>

        <!-- Filter Bar -->
        <div class="px-6 py-3 border-t border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
              <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</label>
              <select
                bind:value={selectedFilter}
                class="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All types</option>
                <option value="issue">Issues</option>
                <option value="pr">Pull requests</option>
                <option value="mention">Mentions</option>
                <option value="review">Review requests</option>
              </select>
            </div>
            {#if unreadCount > 0}
              <button
                onclick={() => markAllAsRead()}
                class="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                Mark all as read
              </button>
            {/if}
          </div>
        </div>
      </div>

      <!-- Notifications List -->
      <div class="divide-y divide-gray-100 dark:divide-gray-700">
        {#if loading}
          <div class="p-16 text-center">
            <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
              <svg class="w-8 h-8 text-gray-400 dark:text-gray-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p class="text-gray-600 dark:text-gray-400">Loading notifications...</p>
          </div>
        {:else if error}
          <div class="p-16 text-center">
            <div class="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <svg class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Error loading notifications</h3>
            <p class="text-gray-600 dark:text-gray-400">{error}</p>
            <button
              onclick={() => fetchNotifications()}
              class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        {:else if filteredNotifications().length === 0}
          <div class="p-16 text-center">
            <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
              <svg class="w-8 h-8 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {#if activeTab === 'new'}
                All caught up!
              {:else}
                Inbox is empty
              {/if}
            </h3>
            <p class="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {#if activeTab === 'new'}
                You're all up to date with your notifications. Great job staying on top of things!
              {:else}
                When you get notifications, they'll appear here.
              {/if}
            </p>
          </div>
        {:else}
          {#each filteredNotifications() as notification}
            <div
              class="group relative px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-150 {notification.status === 'unread' ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-600' : 'border-l-4 border-l-transparent'}"
            >
              <div class="flex gap-4">
                <!-- Status Indicator -->
                <div class="flex flex-col items-center gap-2 pt-1">
                  {#if notification.status === 'unread'}
                    <div class="w-2.5 h-2.5 bg-blue-600 rounded-full"></div>
                  {:else}
                    <div class="w-2.5 h-2.5 bg-transparent rounded-full"></div>
                  {/if}
                </div>

                <!-- Type Icon -->
                <div class="flex-shrink-0 pt-1">
                  <div class="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center {getTypeColor(notification.type as NotificationType)}">
                    {@html getTypeIcon(notification.type as NotificationType)}
                  </div>
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <!-- Repository -->
                  <div class="flex items-center gap-2 mb-2">
                    <a href={notification.url} class="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 hover:underline">
                      {notification.repositoryOwner} / {notification.repositoryName}
                    </a>
                  </div>

                  <!-- Title -->
                  <h3 class="text-base font-medium text-gray-900 dark:text-gray-100 mb-1 leading-snug">
                    {notification.title}
                  </h3>

                  <!-- Description & Metadata -->
                  <div class="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                    <span>{notification.description}</span>
                    <span class="text-gray-400 dark:text-gray-500">•</span>
                    <div class="flex items-center gap-1.5">
                      <img
                        src={getAvatarUrl(notification.authorUsername)}
                        alt={notification.authorUsername}
                        class="w-4 h-4 rounded-full"
                      />
                      <span class="font-medium">{notification.authorUsername}</span>
                    </div>
                    <span class="text-gray-400 dark:text-gray-500">•</span>
                    <span class="text-gray-500 dark:text-gray-400">{getTimeAgo(notification.createdAt)}</span>
                  </div>
                </div>

                <!-- Actions Menu -->
                <div class="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  {#if notification.status === 'unread'}
                    <button
                      onclick={() => markAsRead(notification.id)}
                      class="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                      title="Mark as read"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                    </button>
                  {/if}
                  <button
                    onclick={() => deleteNotification(notification.id)}
                    class="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    title="Delete"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          {/each}
        {/if}
      </div>
    </div>

    <!-- Help Text -->
    {#if filteredNotifications().length > 0}
      <div class="mt-6 text-center">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredNotifications().length} {filteredNotifications().length === 1 ? 'notification' : 'notifications'}
        </p>
      </div>
    {/if}
  </div>
</div>
